import router from "@/router";
import { useAuthStore } from "@/stores/auth";
import axios from "axios";

interface TokenResponse {
  access: string;
  refresh: string;
}

interface VoteObj {
  title: string;
  description: string;
  vote_type: string;
  required_response: number;
  response_deadline: string;
  anonymous: boolean;
  status: string;
}

// const baseUrl = "http://127.0.0.1:8000/api/";

// const baseUrl = "https://foxwood-neighborhood.herokuapp.com/api/";

const baseUrl = process.env.VUE_APP_API_BASE_URL as string;

const axiosInstance = axios.create({
  baseURL: baseUrl,
  timeout: 5000,
  headers: {
    Authorization: localStorage.getItem("token")
      ? "JWT " + localStorage.getItem("token")
      : false,
    "Content-Type": "application/json",
    accept: "application/json",
  },
});
/** Add the token to every outgoing request */
axiosInstance.interceptors.request.use(function (config) {
  const token = localStorage.getItem("token");
  if (config.headers)
    config.headers.Authorization = token ? `JWT ${token}` : "";
  return config;
});

axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  async function (error) {
    const originalRequest = error.config;

    // Handle network errors or undefined responses - don't logout, just reject
    if (!error.response) {
      console.log("Network error or no response");
      return Promise.reject(error);
    }

    console.log(error.response);
    console.log(originalRequest.url);

    // If refresh token request itself failed with invalid token, clear tokens and reject
    if (
      error.response.data?.code === "token_not_valid" &&
      error.response.status === 401 &&
      originalRequest.url === "/token/refresh/"
    ) {
      console.log("Refresh token is invalid, clearing tokens");
      localStorage.removeItem("token");
      localStorage.removeItem("refresh");
      return Promise.reject(error);
    }

    // If a regular request got a 401 with invalid token, try to refresh
    if (
      error.response.data?.code === "token_not_valid" &&
      error.response.status === 401 &&
      originalRequest.url !== "/token/refresh/"
    ) {
      const refreshToken = localStorage.getItem("refresh");
      if (refreshToken && refreshToken !== "undefined") {
        try {
          const tokenParts = JSON.parse(
            window.atob(refreshToken.split(".")[1])
          );
          const now = Math.ceil(Date.now() / 1000);

          if (tokenParts.exp > now) {
            console.log("Attempting to refresh token");
            try {
              const response = await axiosInstance.post("/token/refresh/", {
                refresh: refreshToken,
              });
              console.log("Token refreshed successfully");
              localStorage.setItem("token", response.data.access);
              axiosInstance.defaults.headers.common["Authorization"] =
                "JWT " + response.data.access;
              originalRequest.headers["Authorization"] =
                "JWT " + response.data.access;

              return axiosInstance(originalRequest);
            } catch (refreshErr) {
              // Refresh failed - but only redirect to login if it's an auth error
              // Network errors should just be rejected
              console.log("Error refreshing token:", refreshErr);
              if ((refreshErr as any).response?.status === 401) {
                const auth = useAuthStore();
                auth.logout();
                router.push({ path: "/login", replace: true });
              }
              return Promise.reject(refreshErr);
            }
          } else {
            console.log("Refresh token has expired");
            const auth = useAuthStore();
            auth.logout();
            router.push({ path: "/login", replace: true });
            return Promise.reject(new Error("Refresh token expired"));
          }
        } catch (parseErr) {
          console.log("Error parsing refresh token:", parseErr);
          return Promise.reject(error);
        }
      } else {
        console.log("No refresh token available");
        router.push("/login");
        return Promise.reject(error);
      }
    }

    // For all other errors, just reject
    return Promise.reject(error);
  }
);

export const HoodApi = {
  login: async (email: string, password: string): Promise<TokenResponse> => {
    try {
      console.log("We should be loggin in");
      const response = await axios.post(`${baseUrl}token/`, {
        email,
        password,
      });
      localStorage.setItem("token", response.data.success);
      localStorage.setItem("refresh", response.data.refresh);
      return response.data;
    } catch (error) {
      console.log(error);
      throw error;
    }
  },
  register_user: async (userinfo: any, house_info: House): Promise<any> => {
    try {
      const response = await axios.post(`${baseUrl}user/register/`, {
        user: userinfo,
        house: house_info,
      });
      console.log(response);
      return true;
    } catch (error) {
      console.log(error);
      throw error;
    }
  },
  change_profile_pic: async (file: any): Promise<any> => {
    try {
      const response = await axiosInstance.post(
        `${baseUrl}user_details/`,
        file
      );
      console.log(response);
      return response;
    } catch (error) {
      console.log(error);
      throw error;
    }
  },
  request_password_reset: async (email: string) => {
    try {
      const response = await axios.post(`${baseUrl}password_reset/`, {
        email: email,
      });
      console.log(response.data);
      return response.data;
    } catch (error) {
      console.log(error);
      throw error;
    }
  },
  change_password: async (password: string, token: string) => {
    try {
      const response = await axios.post(`${baseUrl}password_reset/confirm/`, {
        password: password,
        token: token,
      });
      console.log(response);
      return response.data;
    } catch (error) {
      console.log(error);
      throw error;
    }
  },
  update_user_details: async (dataObj: any): Promise<any> => {
    try {
      const response = await axiosInstance.post(
        `${baseUrl}user_details/`,
        dataObj
      );
      console.log(response);
      return response;
    } catch (error) {
      console.log(error);
      throw error;
    }
  },
  fetch_user_details: async (): Promise<UserDetails> => {
    try {
      const response = await axiosInstance.get(`${baseUrl}user_details/`);
      console.log(response);
      return response.data;
    } catch (error) {
      console.log(error);
      throw error;
    }
  },
  fetch_posts: async (category = "all", filter?: string): Promise<any> => {
    try {
      const response = await axiosInstance.get(
        `${baseUrl}posts/category/${category}`
      );
      console.log(response);
      return response.data;
    } catch (error) {
      console.log(error);
      throw error;
    }
  },
  get_covenants: async (): Promise<any> => {
    try {
      const response = await axiosInstance.get(`${baseUrl}covenants/`);
      console.log(response.data);
      return response.data;
    } catch (error) {
      console.log(error);
      throw error;
    }
  },
  make_post: async (post: NewPost): Promise<Post> => {
    try {
      const formData = new FormData();
      formData.append("title", post.title);
      formData.append("description", post.description);
      formData.append("type", post.type);
      if (post.image) {
        formData.append("image", post.image);
      }
      // Append additional images
      if (post.images && post.images.length > 0) {
        for (const img of post.images) {
          formData.append("images", img);
        }
      }
      const response = await axiosInstance.post(`${baseUrl}posts/`, formData);
      console.log(response.data);
      return response.data as Post;
    } catch (e) {
      console.log(e);
      throw e;
    }
  },
  delete_post: async (postId: number): Promise<void> => {
    try {
      await axiosInstance.delete(`${baseUrl}posts/${postId}/`);
    } catch (e) {
      console.log(e);
      throw e;
    }
  },
  make_comment: async (comment: {
    post: number;
    content: string;
  }): Promise<Comment> => {
    try {
      const response = await axiosInstance.post(`${baseUrl}comments/`, comment);
      return response.data;
    } catch (e) {
      console.log(e);

      throw e;
    }
  },
  house_lookup: async (term: string): Promise<House[]> => {
    try {
      const response = await axiosInstance.get(
        `${baseUrl}houses?search=${term}`
      );
      console.log(response.data);
      return response.data;
    } catch (error) {
      console.log(error);
      throw error;
    }
  },
  get_votes: async (): Promise<Vote[]> => {
    try {
      const response = await axiosInstance.get(`${baseUrl}votes/`);
      console.log(response.data);
      const votes = response.data;
      return votes;
    } catch (error) {
      console.log(error);
      throw error;
    }
  },
  cast_ballot: async (ballot: BallotObj): Promise<any> => {
    try {
      const response = await axiosInstance.post(
        `${baseUrl}votes/${ballot.vote}/cast_user_ballot/`,
        ballot
      );
      console.log(response);
      return response.data;
    } catch (e) {
      console.log(e);
      throw e;
    }
  },
  create_dues_payment: async (invoice = "na"): Promise<any> => {
    try {
      const response = await axiosInstance.post(`${baseUrl}create_intent/`, {
        invoice: invoice,
      });
      console.log(response.data);
      return response.data;
    } catch (e) {
      console.log(e);
      throw e;
    }
  },
  update_payment_status: async (
    stripe_status: string,
    payment_id: number
  ): Promise<any> => {
    try {
      const response = await axiosInstance.patch(
        `${baseUrl}payments/${payment_id}/`,
        {
          status: stripe_status,
        }
      );
      return response.data;
    } catch (e) {
      console.log(e);
      throw e;
    }
  },
  get_payment_by_stripe_ref: async (stripe_reference: string): Promise<any> => {
    try {
      const response = await axiosInstance.get(
        `${baseUrl}payments/?stripe_reference=${stripe_reference}`
      );
      return response.data;
    } catch (e) {
      console.log(e);
      throw e;
    }
  },
  create_payment_record: async (
    stripe_ref: string,
    stripe_status: string,
    amount: number
  ): Promise<any> => {
    try {
      const response = await axiosInstance.post(`${baseUrl}payments/`, {
        stripe_reference: stripe_ref,
        status: stripe_status,
        amount: amount,
      });
      console.log(response.data);
      return response;
    } catch (e) {
      console.log(e);
      throw e;
    }
  },
  create_a_vote: async (vote: VoteObj): Promise<Vote> => {
    try {
      const response = await axiosInstance.post(`${baseUrl}votes/`, vote);
      return response.data;
    } catch (error) {
      console.log(error);
      throw error;
    }
  },
  create_contact_request: async (
    email: string,
    message: string,
    phone?: string
  ): Promise<any> => {
    try {
      const response = await axiosInstance.post(`${baseUrl}contact/`, {
        sender: email,
        phone: phone ? phone : "",
        message: message,
      });
      console.log(response.data);
      return response.data;
    } catch (error) {
      console.log(error);
      throw error;
    }
  },
  fetch_events: async (): Promise<any> => {
    try {
      const response = await axiosInstance.get(`${baseUrl}events/`);
      console.log(response.data);
      return response.data;
    } catch (error) {
      console.log(error);
      throw error;
    }
  },
  fetch_photos: async (): Promise<any[]> => {
    try {
      const response = await axiosInstance.get(`${baseUrl}photos/`);
      console.log(response.data);
      return response.data;
    } catch (e) {
      console.log(e);
      throw e;
    }
  },
  upload_photo: async (fileObj: any): Promise<any> => {
    try {
      const response = await axiosInstance.post(`${baseUrl}photos/`, fileObj);
      console.log(response.data);
      return response.data;
    } catch (e) {
      console.log(e);
      throw e;
    }
  },
  tag_lookup: async (term: string): Promise<any> => {
    try {
      const response = await axiosInstance.get(
        `${baseUrl}tags/?search=${term}`
      );
      console.log(response.data);
      return response.data;
    } catch (error) {
      console.log(error);
      throw error;
    }
  },
  create_tag: async (tag: string): Promise<any> => {
    try {
      const response = await axiosInstance.post(`${baseUrl}tags/`, {
        text: tag,
      });
      console.log(response.data);
      return response.data;
    } catch (e) {
      console.log(e);
      throw e;
    }
  },
  get_photos_by_tag: async (tag: string): Promise<any> => {
    try {
      const response = await axiosInstance.post(`${baseUrl}photos_by_tag/`, {
        tags: tag,
      });
      console.log(response.data);
      return response.data;
    } catch (e) {
      console.log(e);
      throw e;
    }
  },

  // Proposals API
  fetch_proposals: async (): Promise<ProposalListItem[]> => {
    try {
      const response = await axiosInstance.get(`${baseUrl}proposals/`);
      return response.data;
    } catch (error) {
      console.log(error);
      throw error;
    }
  },

  fetch_proposal: async (id: number): Promise<Proposal> => {
    try {
      const response = await axiosInstance.get(`${baseUrl}proposals/${id}/`);
      return response.data;
    } catch (error) {
      console.log(error);
      throw error;
    }
  },

  create_proposal: async (proposal: NewProposal): Promise<Proposal> => {
    try {
      const response = await axiosInstance.post(
        `${baseUrl}proposals/`,
        proposal
      );
      return response.data;
    } catch (error) {
      console.log(error);
      throw error;
    }
  },

  support_proposal: async (proposalId: number): Promise<SupportResponse> => {
    try {
      const response = await axiosInstance.post(
        `${baseUrl}proposals/${proposalId}/support/`
      );
      return response.data;
    } catch (error) {
      console.log(error);
      throw error;
    }
  },

  withdraw_support: async (proposalId: number): Promise<any> => {
    try {
      const response = await axiosInstance.post(
        `${baseUrl}proposals/${proposalId}/withdraw_support/`
      );
      return response.data;
    } catch (error) {
      console.log(error);
      throw error;
    }
  },

  fetch_my_proposals: async (): Promise<ProposalListItem[]> => {
    try {
      const response = await axiosInstance.get(
        `${baseUrl}proposals/my-proposals/`
      );
      return response.data;
    } catch (error) {
      console.log(error);
      throw error;
    }
  },

  fetch_my_supported: async (): Promise<ProposalListItem[]> => {
    try {
      const response = await axiosInstance.get(
        `${baseUrl}proposals/my-supported/`
      );
      return response.data;
    } catch (error) {
      console.log(error);
      throw error;
    }
  },

  create_proposal_comment: async (
    comment: NewProposalComment
  ): Promise<ProposalComment> => {
    try {
      const response = await axiosInstance.post(
        `${baseUrl}proposal-comments/`,
        comment
      );
      return response.data;
    } catch (error) {
      console.log(error);
      throw error;
    }
  },

  fetch_proposal_comments: async (
    proposalId: number
  ): Promise<ProposalComment[]> => {
    try {
      const response = await axiosInstance.get(
        `${baseUrl}proposal-comments/?proposal=${proposalId}`
      );
      return response.data;
    } catch (error) {
      console.log(error);
      throw error;
    }
  },

  // Homepage Feed API
  fetch_homepage_feed: async (): Promise<HomepageFeed> => {
    try {
      const response = await axiosInstance.get(`${baseUrl}homepage-feed/`);
      return response.data;
    } catch (error) {
      console.log(error);
      throw error;
    }
  },

  // Reactions API
  toggle_reaction: async (
    request: ReactionToggleRequest
  ): Promise<Reaction | { message: string }> => {
    try {
      const response = await axiosInstance.post(
        `${baseUrl}reactions/`,
        request
      );
      return response.data;
    } catch (error) {
      console.log(error);
      throw error;
    }
  },

  get_reactions_for_object: async (
    contentType: string,
    objectId: number
  ): Promise<Record<string, ReactionSummary>> => {
    try {
      const response = await axiosInstance.get(
        `${baseUrl}reactions/for_object/?content_type=${contentType}&object_id=${objectId}`
      );
      return response.data;
    } catch (error) {
      console.log(error);
      throw error;
    }
  },

  // Payment Status API
  fetch_my_payment_status: async (): Promise<PaymentStatusResponse> => {
    try {
      const response = await axiosInstance.get(`${baseUrl}my-payment-status/`);
      return response.data;
    } catch (error) {
      console.log(error);
      throw error;
    }
  },

  // Dues Settings API (public endpoint)
  fetch_dues_settings: async (): Promise<DuesSettingsResponse> => {
    try {
      const response = await axiosInstance.get(`${baseUrl}dues-settings/`);
      return response.data;
    } catch (error) {
      console.log(error);
      throw error;
    }
  },

  // Past Dues Payment API
  create_past_dues_payment: async (
    authorizationId: number
  ): Promise<PastDuesPaymentResponse> => {
    try {
      const response = await axiosInstance.post(
        `${baseUrl}payments/create_past_dues_payment/`,
        { authorization_id: authorizationId }
      );
      return response.data;
    } catch (e) {
      console.log(e);
      throw e;
    }
  },

  // Local Events API (Whitnall Commons proxy)
  fetch_local_events: async (params?: {
    scope?: "local" | "regional" | "all";
    type?: "event" | "special" | "announcement";
    date_from?: string;
    date_to?: string;
    limit?: number;
    offset?: number;
  }): Promise<LocalEventsResponse> => {
    try {
      const response = await axiosInstance.get(`${baseUrl}local-events/`, {
        params,
      });
      return response.data;
    } catch (error) {
      console.log(error);
      throw error;
    }
  },

  // Halloween API
  fetch_halloween_home: async (): Promise<HalloweenHomeResponse> => {
    try {
      const response = await axiosInstance.get(`${baseUrl}halloween/`);
      return response.data;
    } catch (error) {
      console.log(error);
      throw error;
    }
  },

  fetch_halloween_guide: async (
    contest?: "decorated" | "photo_op"
  ): Promise<HalloweenGuideResponse> => {
    try {
      const params = contest ? { contest } : {};
      const response = await axiosInstance.get(`${baseUrl}halloween/guide/`, {
        params,
      });
      return response.data;
    } catch (error) {
      console.log(error);
      throw error;
    }
  },

  fetch_halloween_participant: async (
    id: number
  ): Promise<HalloweenParticipantDetail> => {
    try {
      const response = await axiosInstance.get(
        `${baseUrl}halloween/participant/${id}/`
      );
      return response.data;
    } catch (error) {
      console.log(error);
      throw error;
    }
  },

  cast_halloween_vote: async (
    participantId: number,
    contest: "decorated" | "photo_op"
  ): Promise<HalloweenVoteResponse> => {
    try {
      const response = await axiosInstance.post(
        `${baseUrl}halloween/vote/${participantId}/`,
        { contest }
      );
      return response.data;
    } catch (error) {
      console.log(error);
      throw error;
    }
  },

  fetch_halloween_registration: async (): Promise<HalloweenMyRegistration> => {
    try {
      const response = await axiosInstance.get(`${baseUrl}halloween/register/`);
      return response.data;
    } catch (error) {
      console.log(error);
      throw error;
    }
  },

  submit_halloween_registration: async (
    data: HalloweenRegistration
  ): Promise<{ message: string; participant: HalloweenParticipant }> => {
    try {
      const formData = new FormData();
      if (data.house_name) formData.append("house_name", data.house_name);
      if (data.photo) formData.append("photo", data.photo);
      formData.append("best_decorated", String(data.best_decorated));
      formData.append("photo_op", String(data.photo_op));

      const response = await axiosInstance.post(
        `${baseUrl}halloween/register/`,
        formData
      );
      return response.data;
    } catch (error) {
      console.log(error);
      throw error;
    }
  },

  cancel_halloween_registration: async (): Promise<{ message: string }> => {
    try {
      const response = await axiosInstance.delete(
        `${baseUrl}halloween/register/cancel/`
      );
      return response.data;
    } catch (error) {
      console.log(error);
      throw error;
    }
  },

  fetch_halloween_leaderboard: async (): Promise<HalloweenLeaderboard> => {
    try {
      const response = await axiosInstance.get(
        `${baseUrl}halloween/leaderboard/`
      );
      return response.data;
    } catch (error) {
      console.log(error);
      throw error;
    }
  },

  submit_halloween_quick_registration: async (data: {
    address: string;
    email: string;
    best_decorated: boolean;
    photo_op: boolean;
  }): Promise<{ message: string }> => {
    try {
      const response = await axiosInstance.post(
        `${baseUrl}halloween/quick-register/`,
        data
      );
      return response.data;
    } catch (error) {
      console.log(error);
      throw error;
    }
  },
};
